'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { useSearchParams } from 'react-router-dom';
import {
  Search,
  Filter,
  SlidersHorizontal,
  X,
  Users,
  Cog,
  Luggage,
  Fuel,
  Star,
  Heart,
  RotateCcw,
  MapPin,
  CalendarDays,
} from 'lucide-react';
import { Vehicle, SearchFilterState, VehicleCategory } from '@/types';
import { useCurrency } from '@/components/site/CurrencyContext';
import { useLocale } from '@/lib/i18n';
import { api } from '@/lib/api';
import { CarIllustration } from './CarIllustrations';

interface VehiclesCatalogViewProps {
  vehicles: Vehicle[];
  favorites: string[];
  onToggleFavorite: (id: string) => void;
  onBookVehicle: (vehicle: Vehicle) => void;
  onViewDetails: (vehicle: Vehicle) => void;
}

function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00`);
  return !Number.isNaN(date.getTime());
}

export function VehiclesCatalogView({
  vehicles,
  favorites,
  onToggleFavorite,
  onBookVehicle,
  onViewDetails,
}: VehiclesCatalogViewProps) {
  const { fmt, equiv } = useCurrency();
  const { t } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const pickup = (searchParams.get('pickup') ?? '').trim();
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';
  const datesActive = isIsoDate(from) && isIsoDate(to) && from <= to;
  const [unavailableIds, setUnavailableIds] = useState<string[]>([]);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [filters, setFilters] = useState<SearchFilterState>({
    searchQuery: '',
    category: 'All',
    maxPrice: 800,
    transmission: 'All',
    fuelType: 'All',
    minSeats: 0,
    sortBy: 'recommended',
  });

  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const categories: string[] = ['All', 'Economy', 'SUV', 'Luxury', 'Convertible', 'Electric', 'Van'];
  const transmissions = ['All', 'Automatic', 'Manual'];
  const fuelTypes = ['All', 'Gasoline', 'Hybrid', 'Electric'];

  // The hero search sends pickup/from/to. Ask the API which cars are already
  // booked for that period and hide them from the grid.
  useEffect(() => {
    if (!datesActive) {
      setUnavailableIds([]);
      setCheckingAvailability(false);
      return;
    }

    let cancelled = false;
    setUnavailableIds([]);
    setCheckingAvailability(true);
    api<{ unavailable?: string[] }>(
      `/api/availability/fleet?pickupDate=${encodeURIComponent(from)}&dropoffDate=${encodeURIComponent(to)}`,
    )
      .then((data) => {
        if (!cancelled) {
          setUnavailableIds(Array.isArray(data?.unavailable) ? data.unavailable : []);
        }
      })
      .catch(() => {
        if (!cancelled) setUnavailableIds([]);
      })
      .finally(() => {
        if (!cancelled) setCheckingAvailability(false);
      });

    return () => {
      cancelled = true;
    };
  }, [datesActive, from, to]);

  // Filter & sort logic
  const filteredVehicles = useMemo(() => {
    return vehicles.filter((car) => {
      // Hero search: pickup location (cars without a location stay visible).
      if (pickup && car.location && !car.location.toLowerCase().includes(pickup.toLowerCase())) {
        return false;
      }

      // Hero search: dates. Cars already booked or blocked are hidden.
      if (datesActive && unavailableIds.includes(car.id)) {
        return false;
      }

      // Query filter
      if (
        filters.searchQuery &&
        !car.name.toLowerCase().includes(filters.searchQuery.toLowerCase()) &&
        !car.brand.toLowerCase().includes(filters.searchQuery.toLowerCase()) &&
        !car.category.toLowerCase().includes(filters.searchQuery.toLowerCase())
      ) {
        return false;
      }

      // Category filter
      if (filters.category !== 'All' && car.category !== filters.category) {
        return false;
      }

      // Max price
      if (car.pricePerDay > filters.maxPrice) {
        return false;
      }

      // Transmission
      if (filters.transmission !== 'All' && car.transmission !== filters.transmission) {
        return false;
      }

      // Fuel type
      if (filters.fuelType !== 'All' && car.fuelType !== filters.fuelType) {
        return false;
      }

      // Min seats
      if (filters.minSeats > 0 && car.seats < filters.minSeats) {
        return false;
      }

      return true;
    }).sort((a, b) => {
      if (filters.sortBy === 'price-asc') return a.pricePerDay - b.pricePerDay;
      if (filters.sortBy === 'price-desc') return b.pricePerDay - a.pricePerDay;
      if (filters.sortBy === 'rating') return b.rating - a.rating;
      return 0; // recommended default
    });
  }, [vehicles, filters, pickup, datesActive, unavailableIds]);

  const clearSearchParams = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('pickup');
    next.delete('from');
    next.delete('to');
    setSearchParams(next, { replace: true });
  };

  const resetFilters = () => {
    setFilters({
      searchQuery: '',
      category: 'All',
      maxPrice: 800,
      transmission: 'All',
      fuelType: 'All',
      minSeats: 0,
      sortBy: 'recommended',
    });
    clearSearchParams();
  };

  const hasActiveFilters =
    filters.searchQuery !== '' ||
    filters.category !== 'All' ||
    filters.maxPrice < 800 ||
    filters.transmission !== 'All' ||
    filters.fuelType !== 'All' ||
    filters.minSeats > 0 ||
    pickup !== '' ||
    datesActive;

  return (
    <div className="w-full bg-slate-50/50 min-h-screen py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        {/* Header Breadcrumb & Title */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
            <span>{t('cat.home')}</span>
            <span>/</span>
            <span className="text-[var(--color-primary)] font-semibold">{t('cat.fleet')}</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">
                {t('cat.title')}
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">
                {t('cat.of')} <strong className="text-slate-900">{filteredVehicles.length}</strong> / {vehicles.length} {t('cat.showing')}
              </p>
            </div>

            {/* Mobile Filter Toggle */}
            <button
              type="button"
              onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
              className="lg:hidden flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-700 shadow-xs cursor-pointer self-start"
            >
              <SlidersHorizontal className="w-4 h-4 text-[var(--color-primary)]" />
              <span>{t('cat.filter')} {hasActiveFilters && t('cat.active')}</span>
            </button>
          </div>
        </div>

        {/* Quick Category Segmented Buttons */}
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-3 mb-6">
          {categories.map((cat) => {
            const isSelected = filters.category === cat;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setFilters({ ...filters, category: cat })}
                className={`px-4 py-2 rounded-xl text-xs font-bold tracking-wide transition-all cursor-pointer whitespace-nowrap ${
                  isSelected
                    ? 'bg-[var(--color-primary)] text-white shadow-sm'
                    : 'bg-white border border-slate-200 text-slate-700 hover:border-amber-400'
                }`}
              >
                {cat === 'All' ? t('cat.all') : cat} {cat === 'All' ? `(${vehicles.length})` : ''}
              </button>
            );
          })}
        </div>

        {(pickup || datesActive) && (
          <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-950">
            {pickup && (
              <span className="inline-flex items-center gap-1.5 font-semibold">
                <MapPin className="h-3.5 w-3.5 text-amber-600" />
                {pickup}
              </span>
            )}
            {datesActive && (
              <span className="inline-flex items-center gap-1.5 font-semibold">
                <CalendarDays className="h-3.5 w-3.5 text-amber-600" />
                {from} → {to}
              </span>
            )}
            <span className="text-amber-700">
              {checkingAvailability
                ? 'Checking availability…'
                : datesActive
                  ? `${unavailableIds.length} vehicle${unavailableIds.length === 1 ? '' : 's'} unavailable for these dates`
                  : ''}
            </span>
            <button
              type="button"
              onClick={clearSearchParams}
              className="ml-auto inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-white px-2.5 py-1.5 text-[11px] font-bold text-amber-800 hover:bg-amber-100"
            >
              <X className="h-3 w-3" />
              Clear search
            </button>
          </div>
        )}

        {/* Main 2-Column Layout: Sidebar Filters + Vehicles Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Desktop Filter Sidebar (Span 3) */}
          <aside className={`lg:col-span-3 ${mobileFilterOpen ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-xs sticky top-24 space-y-6">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2 text-slate-900 font-extrabold text-sm">
                  <Filter className="w-4 h-4 text-[var(--color-primary)]" />
                  <span>{t('cat.searchFilters')}</span>
                </div>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="flex items-center gap-1 text-[11px] text-amber-600 hover:text-amber-700 font-semibold cursor-pointer"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span>{t('cat.reset')}</span>
                  </button>
                )}
              </div>

              {/* 1. Keyword Search */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-2">
                  {t('cat.keyword')}
                </label>
                <div className="relative">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                  <input
                    type="text"
                    placeholder={t('cat.keywordPh')}
                    value={filters.searchQuery}
                    onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
                    className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                  />
                  {filters.searchQuery && (
                    <button
                      type="button"
                      onClick={() => setFilters({ ...filters, searchQuery: '' })}
                      className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* 2. Daily Price Range Slider */}
              <div>
                <div className="flex items-center justify-between text-xs mb-2">
                  <span className="font-bold text-slate-700">{t('cat.maxRate')}</span>
                  <span className="font-extrabold text-[var(--color-primary)] font-mono">
                    {fmt(filters.maxPrice)} / day
                  </span>
                </div>
                <input
                  type="range"
                  min="200"
                  max="800"
                  step="10"
                  value={filters.maxPrice}
                  onChange={(e) => setFilters({ ...filters, maxPrice: Number(e.target.value) })}
                  className="w-full accent-[var(--color-primary)] cursor-pointer"
                />
                <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                  <span>{fmt(200)}/day</span>
                  <span>{fmt(800)}/day</span>
                </div>
              </div>

              {/* 3. Transmission */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-2">
                  {t('cat.transmission')}
                </label>
                <div className="grid grid-cols-3 gap-1.5">
                  {transmissions.map((trans) => (
                    <button
                      key={trans}
                      type="button"
                      onClick={() => setFilters({ ...filters, transmission: trans })}
                      className={`py-1.5 text-xs font-semibold rounded-lg border text-center transition-all cursor-pointer ${
                        filters.transmission === trans
                          ? 'border-[var(--color-primary)] bg-amber-50 text-[var(--color-primary)] font-bold'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {trans}
                    </button>
                  ))}
                </div>
              </div>

              {/* 4. Minimum Passenger Seats */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-2">
                  {t('cat.seats')}
                </label>
                <div className="grid grid-cols-4 gap-1.5">
                  {[
                    { label: t('cat.any'), val: 0 },
                    { label: '4+', val: 4 },
                    { label: '5+', val: 5 },
                    { label: '7+', val: 7 },
                  ].map((s) => (
                    <button
                      key={s.val}
                      type="button"
                      onClick={() => setFilters({ ...filters, minSeats: s.val })}
                      className={`py-1.5 text-xs font-semibold rounded-lg border text-center transition-all cursor-pointer ${
                        filters.minSeats === s.val
                          ? 'border-[var(--color-primary)] bg-amber-50 text-[var(--color-primary)] font-bold'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* 5. Fuel / Power Type */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-2">
                  {t('cat.fuel')}
                </label>
                <div className="space-y-1.5">
                  {fuelTypes.map((fuel) => (
                    <label
                      key={fuel}
                      className="flex items-center justify-between text-xs text-slate-600 hover:text-slate-900 cursor-pointer p-1 rounded hover:bg-slate-50"
                    >
                      <span>{fuel}</span>
                      <input
                        type="radio"
                        name="fuelType"
                        checked={filters.fuelType === fuel}
                        onChange={() => setFilters({ ...filters, fuelType: fuel })}
                        className="accent-[var(--color-primary)]"
                      />
                    </label>
                  ))}
                </div>
              </div>

              {/* Sort By Dropdown */}
              <div className="pt-2 border-t border-slate-100">
                <label className="text-xs font-bold text-slate-700 block mb-2">
                  {t('cat.sort')}
                </label>
                <select
                  value={filters.sortBy}
                  onChange={(e) => setFilters({ ...filters, sortBy: e.target.value as any })}
                  className="w-full p-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 font-semibold focus:outline-hidden cursor-pointer"
                >
                  <option value="recommended">{t('cat.sortRec')}</option>
                  <option value="price-asc">{t('cat.sortAsc')}</option>
                  <option value="price-desc">{t('cat.sortDesc')}</option>
                  <option value="rating">{t('cat.sortRating')}</option>
                </select>
              </div>
            </div>
          </aside>

          {/* Vehicles Cards Grid (Span 9) */}
          <main className="lg:col-span-9">
            {filteredVehicles.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center text-[var(--color-primary)] mb-4">
                  <Search className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">
                  {t('cat.empty')}
                </h3>
                <p className="text-xs text-slate-500 max-w-sm mb-6">
                  {t('cat.emptyHint')}
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="px-5 py-2.5 bg-[var(--color-primary)] text-white text-xs font-bold rounded-xl shadow-xs hover:bg-[var(--color-primary-dark)] cursor-pointer"
                >
                  {t('cat.resetAll')}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredVehicles.map((car) => {
                  const isFav = favorites.includes(car.id);
                  const isAvailable = car.status === 'available';

                  return (
                    <div
                      key={car.id}
                      className="bg-white rounded-2xl border border-slate-200/90 hover:border-slate-300 p-4 shadow-xs hover:shadow-lg transition-all duration-300 flex flex-col justify-between group"
                    >
                      <div>
                        {/* Top: Category & Rating & Wishlist */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                              {car.category}
                            </span>
                            <span className="text-slate-300">·</span>
                            <div className="flex items-center gap-1 text-[11px] font-bold text-amber-500">
                              <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                              <span>{car.rating}</span>
                              <span className="text-slate-400 font-normal">({car.reviewsCount})</span>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => onToggleFavorite(car.id)}
                            className="p-1.5 rounded-full hover:bg-slate-100 transition-colors cursor-pointer text-slate-400 hover:text-rose-500"
                            aria-label="Add to favorites"
                          >
                            <Heart
                              className={`w-4 h-4 transition-colors ${
                                isFav ? 'fill-rose-500 text-rose-500' : ''
                              }`}
                            />
                          </button>
                        </div>

                        {/* Car Photographic Image / Vector Fallback */}
                        <div
                          onClick={() => onViewDetails(car)}
                          className="relative w-full h-40 flex items-center justify-center cursor-pointer p-2 rounded-xl bg-slate-50 group-hover:bg-slate-100/70 transition-colors overflow-hidden"
                        >
                          {car.imageUrl ? (
                            <div className="relative w-full h-full">
                              <Image
                                src={car.imageUrl}
                                alt={car.name}
                                fill
                                className="object-cover rounded-lg group-hover:scale-105 transition-transform duration-500"
                                referrerPolicy="no-referrer"
                              />
                            </div>
                          ) : (
                            <CarIllustration
                              type={car.imageType}
                              className="w-full h-auto max-h-32 object-contain group-hover:scale-105 transition-transform duration-300"
                            />
                          )}
                          {!isAvailable && (
                            <div className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded bg-slate-900/80 text-white text-[10px] font-bold uppercase tracking-wider backdrop-blur-xs">
                              {car.status}
                            </div>
                          )}
                        </div>

                        {/* Car Model & Specs */}
                        <div className="mt-4">
                          <h3
                            onClick={() => onViewDetails(car)}
                            className="text-base font-extrabold text-slate-900 tracking-tight hover:text-[var(--color-primary)] transition-colors cursor-pointer"
                          >
                            {car.name}
                          </h3>

                          {/* 3 Specs: Seats, Transmission, Bags */}
                          <div className="flex items-center gap-3 mt-2.5 text-[11px] font-medium text-slate-500">
                            <div className="flex items-center gap-1">
                              <Users className="w-3.5 h-3.5 text-slate-400" />
                              <span>{car.seats} {t('cat.seatsU')}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Cog className="w-3.5 h-3.5 text-slate-400" />
                              <span>{car.transmission}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Luggage className="w-3.5 h-3.5 text-slate-400" />
                              <span>{car.bags} {t('cat.bagsU')}</span>
                            </div>
                          </div>

                          {/* Fuel & MPG row */}
                          <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-400">
                            <div className="flex items-center gap-1">
                              <Fuel className="w-3 h-3 text-slate-400" />
                              <span>{car.fuelType}</span>
                            </div>
                            <span>·</span>
                            <span>{car.mpg}</span>
                          </div>
                        </div>
                      </div>

                      {/* Price & Book Now Row */}
                      <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
                        <div>
                          <span className="text-xl font-black text-[var(--color-primary)] tracking-tight">
                            {fmt(car.pricePerDay)}
                          </span>
                          <span className="text-xs text-slate-400 font-medium"> / day</span>
                          {equiv(car.pricePerDay) ? (
                            <div className="text-[11px] text-slate-400">{equiv(car.pricePerDay)}</div>
                          ) : null}
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => onViewDetails(car)}
                            className="px-2.5 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                          >
                            {t('cat.details')}
                          </button>
                          <button
                            type="button"
                            onClick={() => onBookVehicle(car)}
                            className="px-4 py-2 border border-slate-300 hover:border-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white text-slate-800 text-xs font-bold tracking-wider uppercase rounded-lg transition-all cursor-pointer whitespace-nowrap active:scale-95"
                          >
                            {t('cat.bookNow')}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
